import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Heart,
  Clock,
  Crown,
  ChevronLeft,
  ArrowRight,
  Share2,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { RECIPES, RECIPE_FILTERS } from "../data/recipes";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { useUpgradeModal } from "../components/AppShell";
import { useShareSheet } from "../components/ShareSheet";
import { toast } from "sonner";

// Filters shown on the "All Recipes" tab — no "Saved" since that's its own tab.
const ALL_FILTERS = ["All", "Skin", "Hair", "Body"];

export default function DIY() {
  const { profile } = useAuth();
  const upgrade = useUpgradeModal();
  const share = useShareSheet();
  const [view, setView] = useState("all"); // all | saved
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [saved, setSaved] = useState(new Set());
  const [active, setActive] = useState(null); // recipe id for detail view

  // Deep-link support: /app/diy?recipe=<id> opens that recipe. Used by
  // shared share-sheet URLs.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get("recipe");
    if (target) setActive(target);
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from("saved_recipes")
      .select("recipe_id")
      .eq("user_id", profile.id)
      .then(({ data }) => setSaved(new Set((data || []).map((r) => r.recipe_id))));
  }, [profile?.id]);

  const toggleSave = async (id) => {
    if (!profile?.id) return;
    const isSaved = saved.has(id);
    // Optimistic update
    const next = new Set(saved);
    if (isSaved) next.delete(id);
    else next.add(id);
    setSaved(next);

    if (isSaved) {
      const { error } = await supabase
        .from("saved_recipes")
        .delete()
        .eq("user_id", profile.id)
        .eq("recipe_id", id);
      if (error) {
        // Rollback
        setSaved((s) => new Set([...s, id]));
        toast.error(`Could not unsave: ${error.message}`);
        return;
      }
      toast("Removed from your recipes");
    } else {
      const { error } = await supabase
        .from("saved_recipes")
        .insert({ user_id: profile.id, recipe_id: id });
      if (error) {
        setSaved((s) => {
          const r = new Set(s);
          r.delete(id);
          return r;
        });
        toast.error(`Could not save: ${error.message}`);
        return;
      }
      toast.success("Saved to your recipes");
    }
  };

  // Featured = best free recipe for user's profile
  const featured = useMemo(() => {
    const userType = profile?.skin_type ? "skin" : "hair";
    return (
      RECIPES.find((r) => r.featured && !r.premium && r.type === userType) ||
      RECIPES.find((r) => r.featured && !r.premium) ||
      RECIPES.find((r) => !r.premium)
    );
  }, [profile?.skin_type]);

  // Filtered visible recipes
  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return RECIPES.filter((r) => {
      if (view === "saved" && !saved.has(r.id)) return false;
      if (view === "all" && filter === "Skin" && r.category !== "Skin") return false;
      if (view === "all" && filter === "Hair" && r.category !== "Hair") return false;
      if (view === "all" && filter === "Body" && r.category !== "Body") return false;
      if (!s) return true;
      return (
        r.name.toLowerCase().includes(s) ||
        r.tags.some((t) => t.toLowerCase().includes(s)) ||
        r.ingredients.some((ing) => ing.toLowerCase().includes(s))
      );
    });
  }, [view, filter, search, saved]);

  const free = visible.filter((r) => !r.premium);
  const premium = visible.filter((r) => r.premium);

  if (active) {
    const recipe = RECIPES.find((r) => r.id === active);
    return (
      <RecipeDetail
        recipe={recipe}
        saved={saved.has(active)}
        onBack={() => setActive(null)}
        onSave={() => toggleSave(active)}
        onShare={() =>
          share.open({
            type: "recipe",
            payload: { name: recipe.name, id: recipe.id },
          })
        }
      />
    );
  }

  return (
    <div className="-mt-px" data-testid="diy-page">
      {/* HEADER */}
      <div
        className="relative pt-6 px-5 pb-5"
        style={{ background: "linear-gradient(180deg, #C9748A 0%, #B8657A 100%)" }}
        data-testid="diy-header"
      >
        <h1 className="font-serif text-white text-3xl leading-tight">DIY Beauty Zone</h1>
        <p className="text-white/85 text-sm font-light mt-0.5">
          Studio-grade recipes from your kitchen
        </p>
        {/* Search */}
        <div
          className="bg-white rounded-full mt-4 px-4 py-2.5 flex items-center gap-2"
          data-testid="diy-searchbar"
        >
          <Search size={14} className="text-cocoa/40 shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search recipes, ingredients..."
            data-testid="diy-search-input"
            className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-cocoa/40 text-cocoa"
          />
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {/* All Recipes / Saved tabs */}
        <div className="flex gap-2 mb-4" data-testid="diy-tabs">
          <button
            onClick={() => setView("all")}
            data-testid="diy-tab-all"
            className={`flex-1 rounded-full py-2 text-sm font-medium transition-all ${
              view === "all"
                ? "bg-rose text-white shadow-rose"
                : "bg-white text-cocoa/60 border border-[#f0e8e4]"
            }`}
          >
            All Recipes
          </button>
          <button
            onClick={() => setView("saved")}
            data-testid="diy-tab-saved"
            className={`flex-1 rounded-full py-2 text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
              view === "saved"
                ? "bg-rose text-white shadow-rose"
                : "bg-white text-cocoa/60 border border-[#f0e8e4]"
            }`}
          >
            <Heart size={12} className={view === "saved" ? "fill-white" : ""} />
            Saved
            {saved.size > 0 && (
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${view === "saved" ? "bg-white/25" : "bg-rose/10 text-rose"}`}>
                {saved.size}
              </span>
            )}
          </button>
        </div>

        {/* Category sub-filters — only on All tab */}
        {view === "all" && (
          <div
            className="flex gap-2 mb-4 overflow-x-auto no-scrollbar -mx-1 px-1"
            data-testid="diy-filters"
          >
            {ALL_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`diy-filter-${f.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  filter === f
                    ? "bg-cocoa text-white"
                    : "bg-white text-cocoa/60 border border-[#f0e8e4]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        )}

        {/* Featured Recipe banner — only on All tab with default filter */}
        {featured && view === "all" && filter === "All" && !search && (
          <button
            onClick={() => setActive(featured.id)}
            data-testid="featured-recipe"
            className="block w-full text-left rounded-2xl p-5 mb-5 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #C9748A 0%, #C9975A 100%)",
            }}
          >
            <span
              aria-hidden
              className="absolute -right-2 bottom-0 text-7xl opacity-30 select-none pointer-events-none"
            >
              🌿
            </span>
            <div className="relative">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white font-medium mb-1 flex items-center gap-1">
                <span>⭐</span> Featured Recipe
              </p>
              <h2 className="font-serif text-white text-2xl leading-tight mb-1">
                {featured.name}
              </h2>
              <p className="text-white/85 text-xs font-light mb-3 flex items-center gap-1.5">
                <Clock size={11} /> {featured.prepTime} · {featured.category} ·{" "}
                {featured.tags[0]} · Free
              </p>
              <span
                className="inline-flex items-center gap-1 bg-white rounded-full px-4 py-1.5 text-xs font-medium"
                style={{ color: "#C9748A" }}
              >
                View Recipe <ArrowRight size={12} />
              </span>
            </div>
          </button>
        )}

        {/* Free Recipes */}
        {free.length > 0 && (
          <div className="mb-5">
            <h2 className="font-serif text-2xl" style={{ color: "#2E1608" }}>
              {view === "saved" ? "Saved Recipes" : "Free Recipes"}
            </h2>
            {view === "all" && (
              <p className="text-xs text-cocoa/55 font-light mb-3">
                Made with kitchen ingredients
              </p>
            )}
            {view === "saved" && <div className="mb-2" />}
            <div className="grid grid-cols-2 gap-3">
              {free.map((r) => (
                <RecipeCard
                  key={r.id}
                  recipe={r}
                  saved={saved.has(r.id)}
                  onSave={() => toggleSave(r.id)}
                  onOpen={() => setActive(r.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Premium Recipes */}
        {premium.length > 0 && (
          <div data-testid="premium-recipes-section">
            <h2 className="font-serif text-2xl" style={{ color: "#2E1608" }}>
              Premium Recipes
            </h2>
            <p className="text-xs text-cocoa/55 font-light mb-3">
              Exclusive formulas for glowing skin + hair
            </p>
            <div className="grid grid-cols-2 gap-3">
              {premium.map((r) => (
                <RecipeCard
                  key={r.id}
                  recipe={r}
                  saved={saved.has(r.id)}
                  isPremium={!!profile?.is_premium}
                  onSave={() => toggleSave(r.id)}
                  onOpen={() => {
                    if (r.premium && !profile?.is_premium) {
                      upgrade.open(r.name);
                      return;
                    }
                    setActive(r.id);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {visible.length === 0 && (
          <p
            className="text-center text-sm text-cocoa/55 font-light py-12"
            data-testid="diy-empty"
          >
            {view === "saved"
              ? "No saved recipes yet. Tap the heart on any recipe to save it."
              : "No recipes match this filter."}
          </p>
        )}
      </div>
    </div>
  );
}

/* ---------- recipe card (grid) ---------- */

function RecipeCard({ recipe, saved, isPremium, onSave, onOpen }) {
  const locked = recipe.premium && !isPremium;
  const categoryColor = recipe.premium ? "#6B3FA0" : "#C9748A";

  return (
    <button
      onClick={onOpen}
      data-testid={`recipe-card-${recipe.id}`}
      className="bg-white rounded-2xl border border-[#f0e8e4] overflow-hidden text-left transition-transform active:scale-[0.98] flex flex-col"
    >
      <div
        className="aspect-square w-full flex items-center justify-center text-5xl relative"
        style={{ background: recipe.cardBg }}
      >
        <span className={locked ? "blur-md select-none" : ""}>{recipe.emoji}</span>
        {recipe.premium ? (
          <span
            className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-sm"
            style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
            data-testid={`recipe-crown-${recipe.id}`}
          >
            👑
          </span>
        ) : (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onSave();
            }}
            data-testid={`recipe-save-${recipe.id}`}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/85 flex items-center justify-center cursor-pointer"
          >
            <Heart
              size={14}
              className={saved ? "text-rose fill-rose" : "text-cocoa/30"}
            />
          </span>
        )}
      </div>
      <div className="px-3 py-3">
        <p
          className="text-[10px] uppercase tracking-wider font-medium mb-0.5"
          style={{ color: categoryColor }}
        >
          {recipe.category}
        </p>
        <h3
          className="font-serif text-base leading-tight mb-2"
          style={{ color: "#2E1608" }}
        >
          {recipe.name}
        </h3>
        <div className="flex items-center gap-2">
          {!recipe.premium && (
            <span className="text-[10px] text-cocoa/55 flex items-center gap-1">
              <Clock size={10} /> {recipe.prepTime}
            </span>
          )}
          <span
            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
            style={
              recipe.premium
                ? { background: "#F5E8FD", color: "#6B3FA0" }
                : { background: "#E8FDF0", color: "#1A5C35" }
            }
          >
            {recipe.premium ? "👑 Premium" : "Free"}
          </span>
        </div>
      </div>
    </button>
  );
}

/* ---------- recipe detail view ---------- */

function RecipeDetail({ recipe, saved, onBack, onSave, onShare }) {
  const related = useMemo(
    () => RECIPES.filter((r) => r.id !== recipe.id && r.type === recipe.type && !r.premium).slice(0, 2),
    [recipe.id, recipe.type]
  );

  return (
    <div className="-mt-px pb-24" data-testid={`recipe-detail-${recipe.id}`}>
      {/* Hero */}
      <div
        className="relative"
        style={{ background: recipe.cardBg }}
      >
        <button
          onClick={onBack}
          data-testid="recipe-back"
          className="absolute top-4 left-4 z-10 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center"
        >
          <ChevronLeft size={18} className="text-cocoa" />
        </button>
        <div className="aspect-[16/10] flex items-center justify-center text-8xl">
          {recipe.emoji}
        </div>
      </div>

      <div className="px-5 pt-5 space-y-5">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-rose font-medium mb-1">
            {recipe.category}
          </p>
          <h1 className="font-serif text-3xl leading-tight" style={{ color: "#2E1608" }}>
            {recipe.name}
          </h1>
          <div className="flex items-center gap-3 mt-2 text-xs text-cocoa/55">
            <span className="flex items-center gap-1">
              <Clock size={12} /> {recipe.prepTime}
            </span>
            <span>·</span>
            <span>{recipe.difficulty}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {recipe.tags.map((t) => (
              <span
                key={t}
                className="text-[10px] bg-cream text-cocoa/70 px-2 py-1 rounded-full"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Ingredients */}
        <Section title="Ingredients">
          <ul className="space-y-2">
            {recipe.ingredients.map((ing, i) => (
              <li key={i} className="text-sm text-cocoa font-light flex items-start gap-2">
                <span className="w-1.5 h-1.5 mt-2 rounded-full bg-rose shrink-0" />
                {ing}
              </li>
            ))}
          </ul>
        </Section>

        {/* Steps */}
        <Section title="Instructions">
          <ol className="space-y-3">
            {recipe.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-rose text-white flex items-center justify-center text-[11px] font-medium shrink-0">
                  {i + 1}
                </span>
                <p className="text-sm text-cocoa font-light leading-relaxed pt-0.5">
                  {s}
                </p>
              </li>
            ))}
          </ol>
        </Section>

        {(recipe.tips?.length || 0) > 0 && (
          <Section title="Tips" accent="#1A5C35">
            <ul className="space-y-1.5">
              {recipe.tips.map((t, i) => (
                <li
                  key={i}
                  className="text-xs text-cocoa font-light flex items-start gap-2"
                >
                  <Sparkles size={12} className="text-gold mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {(recipe.cautions?.length || 0) > 0 && (
          <Section title="Cautions" accent="#8B2222">
            <ul className="space-y-1.5">
              {recipe.cautions.map((t, i) => (
                <li
                  key={i}
                  className="text-xs text-cocoa font-light flex items-start gap-2"
                >
                  <AlertCircle size={12} className="text-rose mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Suitability */}
        {(recipe.skinTypes?.length || recipe.hairTypes?.length) > 0 && (
          <Section title="Suitable for">
            <div className="flex flex-wrap gap-1.5">
              {[...(recipe.skinTypes || []), ...(recipe.hairTypes || [])].map((t) => (
                <span
                  key={t}
                  className="text-[10px] uppercase tracking-wider bg-rose/10 text-rose px-2 py-1 rounded-full"
                >
                  {t}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onSave}
            data-testid="detail-save-btn"
            className="rounded-full py-3 text-sm font-medium bg-white border border-rose text-rose flex items-center justify-center gap-1.5"
          >
            <Heart size={14} className={saved ? "fill-rose" : ""} />
            {saved ? "Saved" : "Save"}
          </button>
          <button
            onClick={onShare}
            data-testid="detail-share-btn"
            className="rounded-full py-3 text-sm font-medium text-white shadow-rose flex items-center justify-center gap-1.5"
            style={{ background: "#C9748A" }}
          >
            <Share2 size={14} /> Share
          </button>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="pt-2">
            <h3 className="font-serif text-xl mb-3" style={{ color: "#2E1608" }}>
              Related recipes
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {related.map((r) => (
                <RecipeCard
                  key={r.id}
                  recipe={r}
                  saved={false}
                  onSave={() => {}}
                  onOpen={() => onBack() /* close + caller-controlled */}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, accent = "#C9975A", children }) {
  return (
    <div>
      <p
        className="text-[10px] uppercase tracking-[0.22em] font-medium mb-2"
        style={{ color: accent }}
      >
        {title}
      </p>
      {children}
    </div>
  );
}
